<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxPilotAssistantAPI from '../../../../../api/inboxes/pilotAssistant';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const ROUTING_MODES = {
  always_ai: 'always_ai',
  after_hours: 'after_hours',
  human_only: 'human_only',
};

export default {
  components: {
    SettingsSection,
    NextButton,
    Avatar,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      selectedAssistantId: null,
      routingMode: ROUTING_MODES.always_ai,
      connectedAssistant: null,
    };
  },
  computed: {
    ...mapGetters({
      pilotAssistants: 'pilotAssistants/getRecords',
    }),
    hasConnectedAssistant() {
      return this.connectedAssistant !== null;
    },
    isHumanOnlyMode() {
      return this.routingMode === ROUTING_MODES.human_only;
    },
    canSave() {
      // 仅人工接待模式下不需要选择助手
      if (this.isHumanOnlyMode) {
        return true;
      }
      // 其他模式需要选择助手
      return !!this.selectedAssistantId;
    },
    routingModeOptions() {
      return [
        {
          value: ROUTING_MODES.always_ai,
          label: this.$t(
            'INBOX_MGMT.PILOT_ASSISTANT.ROUTING_MODE.ALWAYS_AI.LABEL'
          ),
          description: this.$t(
            'INBOX_MGMT.PILOT_ASSISTANT.ROUTING_MODE.ALWAYS_AI.DESCRIPTION'
          ),
        },
        {
          value: ROUTING_MODES.after_hours,
          label: this.$t(
            'INBOX_MGMT.PILOT_ASSISTANT.ROUTING_MODE.AFTER_HOURS.LABEL'
          ),
          description: this.$t(
            'INBOX_MGMT.PILOT_ASSISTANT.ROUTING_MODE.AFTER_HOURS.DESCRIPTION'
          ),
        },
        {
          value: ROUTING_MODES.human_only,
          label: this.$t(
            'INBOX_MGMT.PILOT_ASSISTANT.ROUTING_MODE.HUMAN_ONLY.LABEL'
          ),
          description: this.$t(
            'INBOX_MGMT.PILOT_ASSISTANT.ROUTING_MODE.HUMAN_ONLY.DESCRIPTION'
          ),
        },
      ];
    },
    availableAssistants() {
      return this.pilotAssistants || [];
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler() {
        this.fetchPilotAssistantConfig();
      },
    },
  },
  mounted() {
    this.fetchAssistants();
  },
  methods: {
    async fetchAssistants() {
      try {
        await this.$store.dispatch('pilotAssistants/get');
      } catch (error) {
        // ignore
      }
    },
    async fetchPilotAssistantConfig() {
      if (!this.inbox?.id) return;

      this.isLoading = true;
      try {
        const response = await InboxPilotAssistantAPI.get(this.inbox.id);
        const { assistant, routing_mode } = response.data;

        // 设置 routing_mode（无论是否有助手）
        this.routingMode = routing_mode || ROUTING_MODES.always_ai;

        // 如果有助手，设置助手信息
        if (assistant) {
          this.connectedAssistant = assistant;
          this.selectedAssistantId = assistant.id;
        } else {
          this.connectedAssistant = null;
          this.selectedAssistantId = null;
        }
      } catch (error) {
        // 未关联时返回空数据
        this.connectedAssistant = null;
        this.selectedAssistantId = null;
        this.routingMode = ROUTING_MODES.always_ai;
      } finally {
        this.isLoading = false;
      }
    },
    async handleSave() {
      const assistantId =
        this.selectedAssistantId?.id || this.selectedAssistantId;

      // 仅人工接待模式下不需要助手
      if (!this.isHumanOnlyMode && !assistantId) {
        useAlert(this.$t('INBOX_MGMT.PILOT_ASSISTANT.ERRORS.NO_ASSISTANT'));
        return;
      }

      this.isSaving = true;
      try {
        const payload = {
          routingMode: this.routingMode,
        };

        // 只有在非仅人工接待模式下才传递 assistantId
        if (!this.isHumanOnlyMode && assistantId) {
          payload.assistantId = assistantId;
        }

        const response = await InboxPilotAssistantAPI.update(
          this.inbox.id,
          payload
        );
        const { assistant, routing_mode } = response.data;

        // human_only 模式下 assistant 可能为空
        this.connectedAssistant = assistant || null;
        this.routingMode = routing_mode;

        useAlert(this.$t('INBOX_MGMT.PILOT_ASSISTANT.SUCCESS.SAVED'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.PILOT_ASSISTANT.ERRORS.SAVE_FAILED'));
      } finally {
        this.isSaving = false;
      }
    },
    async handleDisconnect() {
      this.isSaving = true;
      try {
        await InboxPilotAssistantAPI.disconnect(this.inbox.id);
        this.connectedAssistant = null;
        this.selectedAssistantId = null;
        this.routingMode = ROUTING_MODES.always_ai;
        useAlert(this.$t('INBOX_MGMT.PILOT_ASSISTANT.SUCCESS.DISCONNECTED'));
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.PILOT_ASSISTANT.ERRORS.DISCONNECT_FAILED')
        );
      } finally {
        this.isSaving = false;
      }
    },
    handleRoutingModeChange(mode) {
      this.routingMode = mode;
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- 已关联的助手信息 -->
    <SettingsSection
      :title="$t('INBOX_MGMT.PILOT_ASSISTANT.CONNECTED_ASSISTANT.TITLE')"
      :sub-title="$t('INBOX_MGMT.PILOT_ASSISTANT.CONNECTED_ASSISTANT.SUBTITLE')"
    >
      <div v-if="isLoading" class="py-4">
        <span class="text-n-slate-11">{{
          $t('INBOX_MGMT.PILOT_ASSISTANT.LOADING')
        }}</span>
      </div>

      <div
        v-else-if="hasConnectedAssistant"
        class="flex items-center gap-4 py-4"
      >
        <Avatar
          :src="connectedAssistant.avatar_url"
          :name="connectedAssistant.name"
          :size="48"
          rounded-full
        />
        <div class="flex-1">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ connectedAssistant.name }}
          </h4>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.PILOT_ASSISTANT.CONNECTED_ASSISTANT.CONNECTED') }}
          </p>
        </div>
        <NextButton
          type="button"
          faded
          ruby
          :is-loading="isSaving"
          @click="handleDisconnect"
        >
          {{ $t('INBOX_MGMT.PILOT_ASSISTANT.DISCONNECT') }}
        </NextButton>
      </div>

      <div v-else class="py-4">
        <multiselect
          v-model="selectedAssistantId"
          :options="availableAssistants"
          track-by="id"
          label="name"
          :placeholder="$t('INBOX_MGMT.PILOT_ASSISTANT.SELECT_ASSISTANT')"
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          selected-label=""
        />
      </div>
    </SettingsSection>

    <!-- 接待规则 -->
    <SettingsSection
      :title="$t('INBOX_MGMT.PILOT_ASSISTANT.ROUTING_RULES.TITLE')"
      :sub-title="$t('INBOX_MGMT.PILOT_ASSISTANT.ROUTING_RULES.SUBTITLE')"
    >
      <div class="space-y-4">
        <div
          v-for="option in routingModeOptions"
          :key="option.value"
          class="relative cursor-pointer rounded-xl outline outline-1 p-4 transition-all duration-200 bg-n-solid-1"
          :class="
            routingMode === option.value
              ? 'outline-n-blue-9'
              : 'outline-n-weak hover:outline-n-strong'
          "
          @click="handleRoutingModeChange(option.value)"
        >
          <div class="flex items-start gap-3">
            <input
              type="radio"
              :checked="routingMode === option.value"
              class="mt-1 h-4 w-4 border-n-slate-6 text-n-brand focus:ring-n-brand focus:ring-offset-0"
              @change="handleRoutingModeChange(option.value)"
            />
            <div>
              <div class="text-sm font-medium text-n-slate-12">
                {{ option.label }}
              </div>
              <div class="text-sm text-n-slate-11 mt-1">
                {{ option.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>

    <!-- 保存按钮 -->
    <SettingsSection :show-border="false">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :is-loading="isSaving"
        :disabled="!canSave"
        @click="handleSave"
      />
    </SettingsSection>
  </div>
</template>
